// Ruta: apps/dashboard/src/pages/auth/LoginPage.tsx
// Login — Dark mode premium con identidad HeyShake

import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Zap } from 'lucide-react'
import { Button, Input } from '@heyshaker/ui'
import { useAuthStore } from '@heyshaker/auth'
import { tokenStorage } from '@heyshaker/auth'
import { authApi } from '@heyshaker/api-client'
import { getBFFErrorMessage } from '@heyshaker/api-client'

const loginSchema = z.object({
  email: z.string().min(1, 'El correo es requerido').email('Ingresa un correo válido'),
  password: z.string().min(1, 'La contraseña es requerida').min(6, 'Mínimo 6 caracteres'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnUrl = searchParams.get('returnUrl') ?? '/overview'
  const { isAuthenticated, setTokens } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) navigate(returnUrl, { replace: true })
  }, [isAuthenticated, navigate, returnUrl])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (data: LoginFormData) => {
    try {
      const res = await authApi.login({ email: data.email, password: data.password })
      const { access_token, refresh_token, user } = res.data
      await tokenStorage.saveRefreshToken(refresh_token)
      setTokens(access_token, refresh_token, user)
      navigate(returnUrl, { replace: true })
    } catch (err) {
      setError('root', { message: getBFFErrorMessage(err) })
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0d14 0%, #0f1117 40%, #131820 100%)',
      padding: '16px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Glow background effects */}
      <div style={{
        position: 'absolute', top: '-200px', left: '-200px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(200,230,46,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-200px', right: '-200px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(34,211,238,0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 64, height: 64, borderRadius: '20px',
            background: 'linear-gradient(135deg, #c8e62e, #a3c020)',
            boxShadow: '0 8px 32px rgba(200,230,46,0.25)',
            marginBottom: '20px',
          }}>
            <Zap style={{ width: 32, height: 32, color: '#0a0d14' }} />
          </div>
          <h1 style={{
            fontSize: '32px', fontWeight: 800, color: '#e8eaed',
            letterSpacing: '-0.5px',
          }}>
            Hey<span style={{ color: '#c8e62e' }}>Shake</span>
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
            Dashboard de Telemetría
          </p>
        </div>

        {/* Login Card */}
        <div style={{
          background: '#1a1f2e',
          borderRadius: '20px',
          padding: '36px',
          border: '1px solid #2a3146',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ marginBottom: '28px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#e8eaed' }}>
              Iniciar sesión
            </h2>
            <p style={{ fontSize: '14px', color: '#8b92a5', marginTop: '4px' }}>
              Ingresa tus credenciales para continuar
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {errors.root && (
              <div style={{
                borderRadius: '10px',
                background: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.2)',
                padding: '12px 16px',
              }}>
                <p style={{ fontSize: '14px', color: '#f87171', fontWeight: 500 }}>
                  {errors.root.message}
                </p>
              </div>
            )}

            <Input
              type="email"
              label="Correo electrónico"
              placeholder="tu@correo.com"
              autoComplete="email"
              autoFocus
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              type="password"
              label="Contraseña"
              placeholder="••••••••"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: isSubmitting
                  ? 'rgba(200,230,46,0.5)'
                  : 'linear-gradient(135deg, #c8e62e, #a3c020)',
                color: '#0a0d14',
                fontSize: '15px',
                fontWeight: 700,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 16px rgba(200,230,46,0.2)',
                letterSpacing: '0.3px',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) e.currentTarget.style.boxShadow = '0 6px 24px rgba(200,230,46,0.35)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(200,230,46,0.2)'
              }}
            >
              {isSubmitting ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#4b5563', marginTop: '32px' }}>
          © {new Date().getFullYear()} HeyShake. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
